import React, { useState, useEffect, useCallback } from 'react';
import CustomerGrid from './CustomerGrid';
import CustomerModal from './CustomerModal';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import ViewToolbar from '../../common/ViewToolbar';
import { useModal } from '../../../hooks/useModal';
import { useDebounce } from '../../../hooks/useDebounce';
import * as customerService from '../../../services/customerService';

/**
 * Self-contained Customers feature view.
 *
 * Before: Dashboard.js owned ALL of this — customer state, search,
 * fetchData switch-case, handleSave switch-case, handleDelete switch-case,
 * modal state, rendering logic.  That's ~100 lines inside the 565-line
 * Dashboard, mixed in with 5 other entities.
 *
 * After:  This view owns its own data, modals, and CRUD.
 * Dashboard just renders <CustomersView /> when activeView === 'customers'.
 *
 * This follows Single Responsibility — this component is only about customers.
 */
export default function CustomersView() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [gridApi, setGridApi] = useState(null);

  const editModal = useModal();
  const deleteModal = useModal();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await customerService.fetchCustomers(debouncedSearchTerm);
      setCustomers(data);
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async (formData) => {
    try {
      if (editModal.record) {
        await customerService.updateCustomer(editModal.record.id, formData);
      } else {
        await customerService.createCustomer(formData);
      }
      editModal.close();
      fetchData();
    } catch (err) {
      console.error('Error saving customer:', err);
      alert('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await customerService.deleteCustomer(deleteModal.record.id);
      deleteModal.close();
      fetchData();
    } catch (err) {
      console.error('Error deleting customer:', err);
      alert('Failed to delete: ' + err.message);
    }
  };

  return (
    <>
      <ViewToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAddNew={() => editModal.open(null)}
        onExportCsv={() => gridApi?.exportToCsv()}
        placeholder="Search customers..."
      />

      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="flex justify-center mt-10">Loading...</div>
        ) : (
          <CustomerGrid
            rowData={customers}
            onEdit={(rec) => editModal.open(rec)}
            onDelete={(rec) => deleteModal.open(rec)}
            onGridReady={setGridApi}
          />
        )}
      </div>

      <CustomerModal
        isOpen={editModal.isOpen}
        onClose={editModal.close}
        onSubmit={handleSave}
        initialData={editModal.record}
        title={editModal.record ? 'Edit Customer' : 'Add New Customer'}
        existingCustomers={customers}
      />

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        recordName={deleteModal.record?.full_name || 'this record'}
      />
    </>
  );
}
